import ax from "./client";
import { buildErrorResponse } from "@/lib/error";
import { HttpStatusCode } from "axios";
import {
  ApiResponse,
  endpoints,
  ISellerSignUpInput,
  ICustomerSignUpInput,
  ISeller,
  OtpReason,
} from "muminsmart-utils";

export async function customerSignup(
  data: ICustomerSignUpInput,
): Promise<ApiResponse<ISeller | null>> {
  try {
    const response = await ax<ISeller>({
      method: "post",
      url: endpoints.auth.customer.signup,
      data,
    });
    return {
      success: true,
      data: response.data,
      message: "Customer Registered Successfully",
    };
  } catch (error: unknown) {
    return buildErrorResponse(
      error,
      "Error fetching categories",
      "Error during customer signup:",
      null,
    );
  }
}

export async function sellerSignup(
  payload: ISellerSignUpInput,
): Promise<ApiResponse<ISeller | null>> {
  try {
    const response = await ax<ISeller>({
      method: "post",
      url: endpoints.auth.seller.signup,
      data: payload,
    });
    if (response.status !== HttpStatusCode.Created) {
      throw new Error("Failed to register seller");
    }
    return {
      success: true,
      data: response.data,
      message: "Seller Registered Successfully",
    };
  } catch (error: unknown) {
    return buildErrorResponse(
      error,
      "Error registering seller",
      "Error during seller signup:",
      null,
    );
  }
}

export async function sendOtp(data: {
  email?: string;
  mobile?: string;
  reason: OtpReason;
}): Promise<ApiResponse<null>> {
  try {
    const response = await ax({
      method: "post",
      url: endpoints.auth.sendOtp,
      data,
    });
    if (response.data.success) {
      return {
        success: true,
        data: null,
        message: "OTP sent successfully",
      };
    }

    return {
      success: false,
      data: null,
      message: response.data.message || "Error sending otp",
    };
  } catch (error: unknown) {
    return buildErrorResponse(
      error,
      "Error sending otp",
      "Error sending email OTP:",
      null,
    );
  }
}

export async function validateOtp(data: {
  email?: string;
  mobile?: string;
  otp: string;
  reason: string;
}): Promise<ApiResponse<null>> {
  try {
    const response = await ax({
      method: "post",
      url: endpoints.auth.validateOtp,
      data,
    });

    return {
      success: true,
      data: null,
      message: response.data?.message || "OTP validated successfully",
    };
  } catch (error: unknown) {
    return buildErrorResponse(
      error,
      "Error validating otp",
      "Error validating OTP:",
      null,
    );
  }
}

export async function sendForgotPasswordOtp(
  email: string,
): Promise<ApiResponse<null>> {
  try {
    const response = await ax({
      method: "post",
      url: endpoints.auth.forgotPassword.sendOtp,
      data: { email },
    });
    return {
      success: true,
      data: null,
      message: response.data?.message || "OTP sent successfully",
    };
  } catch (error: unknown) {
    return buildErrorResponse(
      error,
      "Error sending email",
      "Error refreshing token:",
      null,
    );
  }
}

export async function verifyForgotPasswordOtp(
  email: string,
  otp: string,
): Promise<ApiResponse<{ resetPasswordToken: string } | null>> {
  try {
    const response = await ax({
      method: "post",
      url: endpoints.auth.forgotPassword.verifyOtp,
      data: { email, otp },
    });
    return {
      success: true,
      data: response.data,
      message: response.data?.message || "OTP verified successfully",
    };
  } catch (error: unknown) {
    return buildErrorResponse(
      error,
      "Error verifying OTP",
      "Error refreshing token:",
      null,
    );
  }
}

export async function resetPassword(
  newPassword: string,
  resetPasswordToken: string,
): Promise<ApiResponse<null>> {
  try {
    const response = await ax({
      method: "put",
      url: endpoints.auth.resetPassword,
      data: { newPassword, resetPasswordToken },
    });
    return {
      success: true,
      data: null,
      message: response.data?.message || "Password reset successfully",
    };
  } catch (error: unknown) {
    return buildErrorResponse(
      error,
      "Error resetting password",
      "Error refreshing token:",
      null,
    );
  }
}
